
import { useEffect, useCallback, useRef } from 'react';
import { Node, Edge } from '@xyflow/react';
import { supabaseWhiteboardService } from '@/services/supabaseWhiteboardService';
import { toast } from '@/hooks/use-toast';

interface UseAutosaveProps {
  whiteboardId: string | null;
  nodes: Node[];
  edges: Edge[];
  enabled?: boolean;
  intervalMs?: number;
}

export const useAutosave = ({
  whiteboardId,
  nodes,
  edges,
  enabled = true,
  intervalMs = 30000 // 30 seconds
}: UseAutosaveProps) => {
  const lastSaveRef = useRef<string>('');
  const autosaveTimerRef = useRef<NodeJS.Timeout>();
  const isInitialLoadRef = useRef(true);

  // Create a snapshot of current state for comparison
  const createStateSnapshot = useCallback(() => {
    return JSON.stringify({ nodes, edges });
  }, [nodes, edges]);

  // Save version to database
  const saveVersion = useCallback(async (isAutoSave = true) => {
    if (!whiteboardId) return false;

    try {
      const result = await supabaseWhiteboardService.saveWhiteboard(
        whiteboardId,
        nodes,
        edges,
        isAutoSave,
        isAutoSave ? undefined : `Version ${new Date().toLocaleString()}`
      );

      if (result) {
        lastSaveRef.current = createStateSnapshot();
        if (!isAutoSave) {
          toast({
            title: "Saved",
            description: "Whiteboard saved successfully",
          });
        }
        return true;
      } else {
        if (!isAutoSave) {
          toast({
            title: "Save Failed",
            description: "Could not save the current version",
            variant: "destructive",
          });
        }
        return false;
      }
    } catch (error) {
      console.error('Save error:', error);
      return false;
    }
  }, [whiteboardId, nodes, edges, createStateSnapshot]);

  // Check if state has changed since last save
  const hasStateChanged = useCallback(() => {
    const currentSnapshot = createStateSnapshot();
    return currentSnapshot !== lastSaveRef.current;
  }, [createStateSnapshot]);

  // Perform autosave if changes detected
  const performAutosave = useCallback(() => {
    if (!enabled || !whiteboardId || isInitialLoadRef.current) return;
    
    if (hasStateChanged()) {
      console.log('Autosaving whiteboard...');
      saveVersion(true);
    }
  }, [enabled, whiteboardId, hasStateChanged, saveVersion]);

  // Manual save function that returns a promise
  const manualSave = useCallback(async () => {
    console.log('Manual save triggered');
    return await saveVersion(false);
  }, [saveVersion]);

  // Set up autosave interval
  useEffect(() => {
    if (!enabled || !whiteboardId) return;

    // Clear existing timer
    if (autosaveTimerRef.current) {
      clearInterval(autosaveTimerRef.current);
    }

    // Set up new timer
    autosaveTimerRef.current = setInterval(performAutosave, intervalMs);

    return () => {
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
      }
    };
  }, [enabled, whiteboardId, intervalMs, performAutosave]);

  // Save on significant changes (debounced)
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      lastSaveRef.current = createStateSnapshot();
      return;
    }

    if (!enabled || !whiteboardId) return;

    const debounceTimer = setTimeout(() => {
      if (hasStateChanged()) {
        performAutosave();
      }
    }, 2000); // 2 second debounce

    return () => clearTimeout(debounceTimer);
  }, [nodes, edges, enabled, whiteboardId, hasStateChanged, performAutosave]);

  // Save when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (enabled && whiteboardId && hasStateChanged()) {
        // Note: This is a fire-and-forget save, might not complete
        saveVersion(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, whiteboardId, hasStateChanged, saveVersion]);

  return {
    manualSave,
    hasUnsavedChanges: hasStateChanged,
    isAutosaveEnabled: enabled
  };
};
